import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Store, Mail, Lock, Building, User, Phone, Globe, 
  Loader2, ArrowLeft, ShoppingBag, Zap, Link2, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendorAuth() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    business_name: '', contact_name: '', email: '', password: '', phone: '', website: '', business_type: 'general'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/vendor/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem('vendor_token', data.token);
      toast.success('Welcome back!');
      navigate('/vendor/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupData.business_name || !signupData.contact_name || !signupData.email || !signupData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/vendor/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      localStorage.setItem('vendor_token', data.token);
      toast.success('Vendor account created!');
      navigate('/vendor/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  const features = [
    { icon: Link2, title: 'Link Your Stores', desc: 'Connect Amazon, Walmart, eBay, Shopify & more' },
    { icon: Zap, title: 'Auto-Sync Products', desc: 'AI pulls your catalog automatically via API keys' },
    { icon: Package, title: 'Manual Import', desc: 'Paste any product URL to import instantly' },
    { icon: ShoppingBag, title: 'Sell on Jiffys', desc: 'Products listed directly on our marketplace' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex" data-testid="vendor-auth-page">
      <Link to="/" className="absolute top-6 left-6 text-white/70 hover:text-white flex items-center gap-2 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Jiffys
      </Link>

      {/* Left - Features */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12">
        <div className="max-w-md space-y-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Store className="h-7 w-7 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Vendor Portal</h1>
              <p className="text-emerald-300/80 text-sm">Jiffys Mobile Marketplace</p>
            </div>
          </div>
          <p className="text-white/60 text-lg leading-relaxed">
            Connect your online stores, sync your products, and reach thousands of customers on the Jiffys marketplace.
          </p>
          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={f.title || i} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{f.title}</p>
                  <p className="text-white/50 text-xs">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
              <Store className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Vendor Account</CardTitle>
            <CardDescription>Link your stores & start selling on Jiffys</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="vendor-login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="vendor-signup-tab">Create Account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="email" placeholder="Email" className="pl-10" value={loginData.email}
                      onChange={e => setLoginData({...loginData, email: e.target.value})} data-testid="vendor-login-email" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="password" placeholder="Password" className="pl-10" value={loginData.password}
                      onChange={e => setLoginData({...loginData, password: e.target.value})} data-testid="vendor-login-password" />
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="vendor-login-btn">
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Store className="h-4 w-4 mr-2" />}
                    Sign In to Vendor Portal
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-3">
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input placeholder="Business Name *" className="pl-10" value={signupData.business_name}
                      onChange={e => setSignupData({...signupData, business_name: e.target.value})} data-testid="vendor-signup-business" />
                  </div>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input placeholder="Contact Name *" className="pl-10" value={signupData.contact_name}
                      onChange={e => setSignupData({...signupData, contact_name: e.target.value})} data-testid="vendor-signup-name" />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="email" placeholder="Email *" className="pl-10" value={signupData.email}
                      onChange={e => setSignupData({...signupData, email: e.target.value})} data-testid="vendor-signup-email" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="password" placeholder="Password *" className="pl-10" value={signupData.password}
                      onChange={e => setSignupData({...signupData, password: e.target.value})} data-testid="vendor-signup-password" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input placeholder="Phone" className="pl-10" value={signupData.phone}
                        onChange={e => setSignupData({...signupData, phone: e.target.value})} />
                    </div>
                    <div className="relative">
                      <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input placeholder="Website" className="pl-10" value={signupData.website}
                        onChange={e => setSignupData({...signupData, website: e.target.value})} />
                    </div>
                  </div>
                  <select className="w-full h-10 px-3 border rounded-md text-sm" value={signupData.business_type}
                    onChange={e => setSignupData({...signupData, business_type: e.target.value})} data-testid="vendor-signup-type">
                    <option value="general">General Retailer</option>
                    <option value="auto_parts">Auto Parts</option>
                    <option value="electronics">Electronics</option>
                    <option value="tools">Tools & Equipment</option>
                    <option value="wholesale">Wholesale</option>
                  </select>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="vendor-signup-btn">
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Store className="h-4 w-4 mr-2" />}
                    Create Vendor Account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
